import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, FileText, Loader2, Upload, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const EVENT = {
  slug: "new-event",
  title: "New Event",
  subtitle: "Register for the upcoming OSPF event.",
  amount: 0,
};

export default function EventRegistration() {
  const [searchParams] = useSearchParams();
  const photoRef = useRef(null);
  const proofRef = useRef(null);
  const amount = useMemo(() => {
    const raw = searchParams.get("amount");
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : EVENT.amount;
  }, [searchParams]);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    fee_amount: String(amount || ""),
    transaction_id: "",
  });
  const [photo, setPhoto] = useState(null);
  const [proof, setProof] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setForm((prev) => ({ ...prev, fee_amount: String(amount || "") }));
  }, [amount]);

  const feeAmount = Math.max(Number(form.fee_amount || 0) || 0, 0);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!photo) {
      toast.error("Please upload your photo.");
      return;
    }
    if (feeAmount > 0 && !proof) {
      toast.error("Please upload payment screenshot.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("event_slug", EVENT.slug);
      fd.append("event_title", EVENT.title);
      fd.append("name", form.name.trim());
      fd.append("mobile", form.mobile.trim());
      fd.append("email", form.email.trim());
      fd.append("fee_amount", String(feeAmount));
      fd.append("transaction_id", form.transaction_id.trim());
      fd.append("photo", photo);
      if (proof) fd.append("payment_proof", proof);

      await api.post("/event-registrations", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Registration submitted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-cream-deep flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-xl rounded-3xl bg-white border border-black/5 shadow-premium p-6 sm:p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-green-50 text-green-700 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="mt-5 font-heading text-3xl text-ink">Registration received</h1>
          <p className="mt-3 text-ink-soft leading-relaxed">
            Thank you. Your details have been sent to the OSPF admin panel.
          </p>
          <Link to="/" className="btn-primary mt-6 inline-flex">
            Back to home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream-deep">
      <section className="bg-[#02042B] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/60">
            OSPF upcoming event
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-5xl font-light">
            {EVENT.title} Registration
          </h1>
          <p className="mt-3 text-white/70 max-w-2xl">{EVENT.subtitle}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4">
          <div className="rounded-3xl bg-white border border-black/5 shadow-premium p-5 sm:p-6 lg:sticky lg:top-6">
            <div className="overline mb-3">Fees</div>
            <div className="font-heading text-4xl text-ink">
              {feeAmount > 0 ? `₹${feeAmount}` : "No fee"}
            </div>
            {feeAmount > 0 ? (
              <>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                  Scan the QR, pay the event fee, then upload the payment screenshot and transaction ID.
                </p>
                <div className="mt-5 rounded-2xl border border-black/5 bg-white p-3">
                  <img
                    src="/gpay-qr.jpeg"
                    alt="Google Pay QR"
                    className="w-full aspect-square object-contain"
                  />
                </div>
              </>
            ) : (
              <p className="mt-3 text-sm text-ink-soft leading-relaxed">
                Payment proof is not required for this event right now.
              </p>
            )}
          </div>
        </aside>

        <form onSubmit={submit} className="lg:col-span-8 rounded-3xl bg-white border border-black/5 shadow-premium p-5 sm:p-7 space-y-5">
          <div>
            <div className="overline mb-2">Register here</div>
            <h2 className="font-heading text-2xl text-ink">Participant details</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name">
              <input
                className="input-premium"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                required
                placeholder="Full name"
              />
            </Field>
            <Field label="Mobile no">
              <input
                className="input-premium"
                value={form.mobile}
                onChange={(e) => update("mobile", e.target.value)}
                required
                inputMode="tel"
                placeholder="9876543210"
              />
            </Field>
          </div>

          <Field label="Email Id">
            <input
              className="input-premium"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
              type="email"
              placeholder="name@example.com"
            />
          </Field>

          <Field label="Fees">
            <input
              className="input-premium"
              value={form.fee_amount}
              onChange={(e) => update("fee_amount", e.target.value)}
              inputMode="numeric"
              type="number"
              min="0"
              placeholder="Enter amount if fee is applicable"
            />
          </Field>

          <UploadBox
            label="Photo"
            file={photo}
            accept="image/jpeg,image/png,image/webp"
            inputRef={photoRef}
            icon={UserRound}
            onPick={(file) => setPhoto(file)}
            onClear={() => {
              setPhoto(null);
              if (photoRef.current) photoRef.current.value = "";
            }}
          />

          {feeAmount > 0 ? (
            <>
              <Field label="Transaction ID">
                <input
                  className="input-premium"
                  value={form.transaction_id}
                  onChange={(e) => update("transaction_id", e.target.value)}
                  required
                  placeholder="UPI reference / transaction ID"
                />
              </Field>
              <UploadBox
                label="Payment screenshot"
                file={proof}
                accept="image/jpeg,image/png,application/pdf"
                inputRef={proofRef}
                icon={FileText}
                onPick={(file) => setProof(file)}
                onClear={() => {
                  setProof(null);
                  if (proofRef.current) proofRef.current.value = "";
                }}
              />
            </>
          ) : null}

          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center">
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Registration"
            )}
          </button>
        </form>
      </section>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-soft mb-2">{label}</span>
      {children}
    </label>
  );
}

function UploadBox({ label, file, accept, inputRef, icon: Icon, onPick, onClear }) {
  return (
    <div>
      <div className="text-sm font-medium text-ink-soft mb-2">{label}</div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          if (picked) onPick(picked);
        }}
      />
      {file ? (
        <div className="rounded-2xl border border-black/5 bg-cream-deep px-4 py-3 flex items-center justify-between gap-3">
          <span className="text-sm text-ink-soft truncate">{file.name}</span>
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-1 text-xs font-medium text-red-600"
          >
            <X className="w-3.5 h-3.5" />
            Remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full rounded-2xl border-2 border-dashed border-brand/35 bg-brand/5 px-5 py-6 text-center text-brand hover:bg-brand/10 transition"
        >
          <Icon className="w-6 h-6 mx-auto mb-2" />
          <span className="font-medium">
            <Upload className="w-4 h-4 inline mr-1" />
            Upload {label}
          </span>
        </button>
      )}
    </div>
  );
}
